import { forwardRef } from 'react';

const sizes = {
  sm: { size: 40, strokeWidth: 4, fontSize: 'text-xs' },
  md: { size: 60, strokeWidth: 6, fontSize: 'text-sm' },
  lg: { size: 80, strokeWidth: 8, fontSize: 'text-base' },
  xl: { size: 100, strokeWidth: 10, fontSize: 'text-lg' },
};

const ConfidenceRing = forwardRef(({
  confidence,
  size = 'md',
  className = '',
  showLabel = true,
  ...props
}, ref) => {
  const config = sizes[size] || sizes.md;
  const { size: dimension, strokeWidth, fontSize } = config;

  // Normalize confidence to 0-100 range
  const normalizedConfidence = Math.min(Math.max(confidence, 0), 10) * 10;

  // Calculate circle parameters
  const radius = (dimension - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (normalizedConfidence / 100) * circumference;

  // Determine color based on confidence
  const getColor = () => {
    if (normalizedConfidence >= 75) return '#00FF88'; // Success green
    if (normalizedConfidence >= 50) return '#FFB800'; // Warning yellow
    return '#FF2D55'; // Danger red
  };

  const color = getColor();

  return (
    <div ref={ref} className={`inline-flex flex-col items-center ${className}`} {...props}>
      <div className="relative" style={{ width: dimension, height: dimension }}>
        {/* Background circle */}
        <svg
          width={dimension}
          height={dimension}
          className="transform -rotate-90"
        >
          <circle
            cx={dimension / 2}
            cy={dimension / 2}
            r={radius}
            stroke="#2D3452"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          {/* Progress circle */}
          <circle
            cx={dimension / 2}
            cy={dimension / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
            style={{
              filter: `drop-shadow(0 0 6px ${color}40)`,
            }}
          />
        </svg>

        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-bold font-mono ${fontSize}`} style={{ color }}>
            {confidence}/10
          </span>
        </div>
      </div>

      {showLabel && (
        <p className="mt-2 text-xs text-text-secondary font-medium">
          Confidence
        </p>
      )}
    </div>
  );
});

ConfidenceRing.displayName = 'ConfidenceRing';

export default ConfidenceRing;
